/**
 * Money desk - pure view logic (ACCOUNTS-PHASE-1-BUILD-V1; brief screens
 * 6.1-6.3, mockups M1-M3). No fetch, no React: wire types, the access
 * decision, timeline composition, labels, the close-conclusion headline
 * (LES-015), and the draft (de)serialization (LES-012). Money arrives in
 * INTEGER MINOR UNITS and is formatted ONLY through the shared billing
 * formatter -- this module never re-derives a money figure; the equation
 * renders the server's own numbers (the drawer-law posture).
 */

import { formatMinorUnitsInr } from '@/dashboard/billing/billing-entry-view';
import type { DashboardMe } from '@/dashboard/types';

// ---- Wire types (mirror api/routes/accounts.ts) ---------------------------

export interface MoneyCollectionWire {
  readonly paymentId: number;
  readonly occurredAt: string;
  readonly amountMinorUnits: number;
  readonly currency: string;
  readonly patientLabel: string | null;
  readonly source: 'billing';
}

export interface MoneyDrawerEntryWire {
  readonly comment?: string | null;
  readonly id: number;
  readonly clinicId?: number;
  readonly businessDate?: string;
  readonly kind: 'spent' | 'petty_cash' | 'reversal' | 'bank_deposit';
  readonly category: string | null;
  readonly particulars: string;
  readonly paidTo: string | null;
  readonly amountMinorUnits: number;
  readonly currency: string;
  readonly proofState: 'attached' | 'pending' | 'missing';
  readonly proofReferenceNumber: string | null;
  readonly proofImageId: number | null;
  readonly noProofApprovedRole: string | null;
  readonly reversesEntryId: number | null;
  readonly reversedByEntryId: number | null;
  readonly recordedAt: string;
  readonly recordedByRole: string;
  readonly recordedByLabel?: string | null;
}

export interface MoneyClosedDayWire {
  readonly dayStartMinorUnits: number;
  readonly derivedCloseMinorUnits: number;
  readonly countedCloseMinorUnits: number;
  readonly differenceMinorUnits: number;
  readonly differenceReasonCategory: string | null;
  readonly differenceReasonText: string | null;
  readonly acknowledgedByLabel: string;
  readonly acknowledgedByRole: string;
  readonly acknowledgedAt: string;
  readonly verifiedByDoctorName: string | null;
  readonly verifiedAt: string | null;
}

export interface MoneyTaskWire {
  readonly kind:
    | 'close_prior_day'
    | 'close_day'
    | 'missing_proof'
    | 'deposit_due';
  readonly date: string | null;
  readonly entryId: number | null;
  readonly amountMinorUnits: number | null;
}

export interface MoneyDayWire {
  readonly clinicId: number;
  readonly businessDate: string;
  readonly isToday: boolean;
  readonly dayStatus:
    | 'no_chain'
    | 'pending_prior_day'
    | 'open'
    | 'awaiting_acknowledgment'
    | 'acknowledged';
  readonly pendingPriorDates: readonly string[];
  readonly dayStart: {
    readonly minorUnits: number;
    readonly currency: string;
    readonly source: 'carryover' | 'first_count';
    readonly carriedFromDate: string;
  } | null;
  readonly equation: {
    readonly cashInMinorUnits: number;
    readonly spentMinorUnits: number;
    readonly sentMinorUnits: number;
    readonly receivedMinorUnits: number;
    readonly cashNowMinorUnits: number;
  } | null;
  readonly asOf: string;
  readonly collections: readonly MoneyCollectionWire[];
  readonly collectionsCapped: boolean;
  /** Q-02=A (RECEPTION-OS-BUILD-V1): the day's recorded-collections total
   *  -- EVERY payment method, voids excluded, this clinic. Payments-plane
   *  truth (present whatever the drawer chain state); renders on the
   *  Money page ONLY -- no other surface renders this total. */
  readonly recordedCollections: {
    readonly totalMinorUnits: number;
    readonly capped: boolean;
  };
  readonly drawerEntries: readonly MoneyDrawerEntryWire[];
  readonly closedDay: MoneyClosedDayWire | null;
  readonly tasks: readonly MoneyTaskWire[];
  readonly nudge: {
    readonly due: boolean;
    readonly thresholdMinorUnits: number;
  };
  readonly config: {
    readonly expenseCategories: readonly string[];
    readonly depositBanks?: readonly string[];
    readonly pettyCashLabel: string;
    readonly differenceReasonCategories: readonly string[];
    readonly autoCloseTime: string;
    readonly verifierRequired: boolean;
    readonly verifierRole: string;
    readonly doctors: readonly { readonly id: number; readonly name: string }[];
    readonly approvers: readonly {
      readonly id: number;
      readonly label: string;
      readonly role: string;
    }[];
    readonly proofImageMaxBytes: number;
    readonly proofImageAllowedTypes: readonly string[];
  };
  readonly viewer: {
    readonly role: string;
    readonly canWrite: boolean;
    readonly canFirstCount: boolean;
  };
}

// ---- Access decision (the RequireBillingEntry idiom: role-only UX gate;
// backend route auth stays the real boundary. Recorded gap: an org that
// retargets the counter role (C6) to an exotic role still works through
// the backend; the nav approximation hides the link for it.) -------------

export const MONEY_DESK_ROLES: readonly DashboardMe['role'][] = [
  'receptionist',
  'coc',
  'owner',
];

export type MoneyDeskAccessDecision =
  | { readonly kind: 'wait' }
  | { readonly kind: 'redirect'; readonly to: string }
  | { readonly kind: 'render' };

export function decideMoneyDeskAccess(input: {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
}): MoneyDeskAccessDecision {
  if (input.loading) return { kind: 'wait' };
  if (input.error !== null || input.me === null) {
    return { kind: 'redirect', to: '/login' };
  }
  if (!MONEY_DESK_ROLES.includes(input.me.role)) {
    return { kind: 'redirect', to: '/dashboard' };
  }
  return { kind: 'render' };
}

// ---- Formatting -----------------------------------------------------------

/** 'Rs 28,077' -- the mockups' display word over the shared formatter's
 *  integer-safe digits ('INR 28,077' -> 'Rs 28,077'). ONE formatter does
 *  the math; this only swaps the display word (M1 label table). */
export function formatRs(minorUnits: number): string {
  return formatMinorUnitsInr(minorUnits).replace(/^INR/, 'Rs');
}

/** Signed digits without the currency word (equation tiles). */
export function formatTileAmount(minorUnits: number): string {
  return formatRs(minorUnits).replace(/^(?:Rs|₹)\s/, '');
}

const IST_TIME = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  hour: 'numeric',
  minute: '2-digit',
});

const IST_DAY = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

/** '2:40 pm' -> '2:40 PM' (IST wall time of an instant). */
export function istTimeLabel(instantIso: string): string {
  const t = Date.parse(instantIso);
  if (Number.isNaN(t)) return '';
  return IST_TIME.format(new Date(t)).toUpperCase();
}

/** 'Thu 31 Jul' for an IST business date ('YYYY-MM-DD'). */
export function istDateLabel(businessDate: string): string {
  const t = Date.parse(`${businessDate}T12:00:00+05:30`);
  if (Number.isNaN(t)) return businessDate;
  return IST_DAY.format(new Date(t)).replace(/,/g, '');
}

// ---- Labels (the brief's display-language law; first-day words) ----------

export const DAY_STATUS_LABELS: Record<MoneyDayWire['dayStatus'], string> = {
  no_chain: 'No first count',
  pending_prior_day: 'Earlier day open',
  open: 'Open',
  awaiting_acknowledgment: 'Awaiting acknowledgement',
  acknowledged: 'Closed',
};

export const DIFFERENCE_REASON_LABELS: Record<string, string> = {
  change_given_entry_missed: 'Change given, entry missed',
  counting_error: 'Counting error',
  other: 'Other',
};

export function differenceReasonLabel(token: string): string {
  return DIFFERENCE_REASON_LABELS[token] ?? token.replace(/_/g, ' ');
}

/** One-word proof chip at list level (founder simplicity rule 4). */
export function proofChipLabel(
  entry: Pick<MoneyDrawerEntryWire, 'kind' | 'proofState'>,
): string {
  if (entry.kind === 'reversal') return 'Reverses';
  if (entry.proofState === 'attached') return 'Voucher';
  if (entry.proofState === 'pending') return 'No photo yet';
  return 'No photo yet';
}

// ---- Timeline composition (the M1 entries table + drill rows) -------------

export interface MoneyTimelineRow {
  readonly key: string;
  /** Sort instant; null = the day-start row (always first). */
  readonly atIso: string | null;
  readonly timeLabel: string;
  readonly typeToken:
    | 'day_start'
    | 'cash_in'
    | 'spent'
    | 'petty_cash'
    | 'reversal'
    | 'bank_deposit';
  readonly typeLabel: string;
  readonly what: string;
  readonly sub: string | null;
  readonly entry: MoneyDrawerEntryWire | null;
  readonly payment: MoneyCollectionWire | null;
  /** Signed drawer effect in minor units (day-start row: 0). */
  readonly signedMinorUnits: number;
  /** Drawer figure AFTER this row (the running fold; null without a
   *  resolved day start). */
  readonly drawerAfterMinorUnits: number | null;
}

/**
 * Compose the unified day timeline: day-start first, then collections and
 * drawer entries merged by instant, with the running drawer fold. Returns
 * rows OLDEST-FIRST; the table renders them newest-first (M1).
 */
export function composeTimeline(
  wire: Pick<
    MoneyDayWire,
    | 'dayStart'
    | 'collections'
    | 'drawerEntries'
    | 'config'
  >,
): readonly MoneyTimelineRow[] {
  const rows: MoneyTimelineRow[] = [];
  const events: Array<Omit<MoneyTimelineRow, 'drawerAfterMinorUnits'>> = [];
  for (const p of wire.collections) {
    events.push({
      key: `pay-${p.paymentId}`,
      atIso: p.occurredAt,
      timeLabel: istTimeLabel(p.occurredAt),
      typeToken: 'cash_in',
      typeLabel: 'Cash in',
      what: p.patientLabel ?? `Payment #${p.paymentId}`,
      sub: 'Billing - read only',
      entry: null,
      payment: p,
      signedMinorUnits: p.amountMinorUnits,
    });
  }
  for (const e of wire.drawerEntries) {
    const typeLabel =
      e.kind === 'petty_cash'
        ? wire.config.pettyCashLabel
        : e.kind === 'reversal'
          ? 'Reversed'
          : e.kind === 'bank_deposit' ? 'Bank deposit' : 'Spent';
    events.push({
      key: `entry-${e.id}`,
      atIso: e.recordedAt,
      timeLabel: istTimeLabel(e.recordedAt),
      typeToken: e.kind,
      typeLabel,
      what: e.particulars,
      sub: e.kind === 'reversal' ? `Reverses entry ${e.reversesEntryId}` : e.paidTo,
      entry: e,
      payment: null,
      signedMinorUnits:
        e.kind === 'reversal' ? e.amountMinorUnits : -e.amountMinorUnits,
    });
  }
  events.sort((a, b) => {
    const ta = a.atIso === null ? 0 : Date.parse(a.atIso);
    const tb = b.atIso === null ? 0 : Date.parse(b.atIso);
    if (ta !== tb) return ta - tb;
    return a.key < b.key ? -1 : 1;
  });
  let running = wire.dayStart === null ? null : wire.dayStart.minorUnits;
  if (wire.dayStart !== null) {
    rows.push({
      key: 'day-start',
      atIso: null,
      timeLabel: '',
      typeToken: 'day_start',
      typeLabel: 'Day start',
      what:
        wire.dayStart.source === 'first_count'
          ? 'First count'
          : `Carried from ${istDateLabel(wire.dayStart.carriedFromDate)}`,
      sub: 'never typed',
      entry: null,
      payment: null,
      signedMinorUnits: 0,
      drawerAfterMinorUnits: running,
    });
  }
  for (const ev of events) {
    if (running !== null) running += ev.signedMinorUnits;
    rows.push({ ...ev, drawerAfterMinorUnits: running });
  }
  return rows;
}

// ---- The To-do rail derivation (FRONT-OFFICE-OS-V2-BUILD-V1, FO-7 + walk
// finding F4) ----------------------------------------------------------------
//
// The founder's F4 evidence: the rail said "Nothing waiting." while the hero
// named an owed first count on the SAME screen. The server task list
// (day.tasks) lawfully carries close/photo/deposit duties but no first-count
// kind -- and the #413 accounts routes are byte-untouched law here -- so the
// rail's DISPLAY list is derived CLIENT-side from the same wire: the day's
// own no_chain status contributes the first-count duty. No number, actor, or
// permission is invented; this reads state the wire already serves.

export interface MoneyTodoItem {
  readonly kind: 'first_count' | MoneyTaskWire['kind'];
  readonly label: string;
  readonly tone: 'warn' | 'info' | 'ok';
  /** The server task this row mirrors; null for the derived first-count row. */
  readonly task: MoneyTaskWire | null;
}

export const MONEY_TODO_FIRST_COUNT_LABEL = 'Record the first count';

export function deriveMoneyTodoItems(
  day: Pick<MoneyDayWire, 'dayStatus' | 'tasks' | 'config'>,
): readonly MoneyTodoItem[] {
  const items: MoneyTodoItem[] = [];
  if (day.dayStatus === 'no_chain') {
    items.push({
      kind: 'first_count',
      label: MONEY_TODO_FIRST_COUNT_LABEL,
      tone: 'warn',
      task: null,
    });
  }
  for (const t of day.tasks) {
    items.push({
      kind: t.kind,
      label:
        t.kind === 'close_prior_day'
          ? `Close ${istDateLabel(t.date ?? '')}`
          : t.kind === 'close_day'
            ? `Close day - ${day.config.autoCloseTime} IST`
            : t.kind === 'deposit_due'
              ? `Deposit ${formatRs(t.amountMinorUnits ?? 0)}`
              : 'Add voucher',
      tone:
        t.kind === 'missing_proof' || t.kind === 'close_prior_day'
          ? 'warn'
          : t.kind === 'deposit_due'
            ? 'info'
            : 'ok',
      task: t,
    });
  }
  return items;
}

// ---- F4 empty-state language (what happened + the next action for the role
// that can act, or WHO can -- the empty-state discipline S-4). The actor line
// never names a tenant role in code: the true actor set is org config
// server-side (accounts.first_count_roles feeds viewer.canFirstCount); the
// copy names the class in first-day words, per the founder's dictation. ----

// ---- Q-02=A (RECEPTION-OS-BUILD-V1): the collections-total strip ----------
//
// The ruled ONE render of the day's collections total: Money page only.
// EVERY method counts (cash, UPI, card, ...), voids are excluded, the
// figure is this clinic's own -- all derived SERVER-side through THE ONE
// window-payments loader; this layer only titles and formats it. The
// row-level evidence for the total lives in Billing (recent payments,
// every method); the note names that door as TEXT -- the drawer timeline
// below stays CASH-only rows, a different concept.

export const RECORDED_COLLECTIONS_NOTE =
  'Every payment method, voids excluded. Rows live in Billing.';
export const RECORDED_COLLECTIONS_CAPPED_NOTE =
  'Large day - scan bound hit; this figure is a lower bound.';

/** 'Collected today' on the live day; the dated form on a past day. */
export function recordedCollectionsTitle(
  isToday: boolean,
  businessDate: string,
): string {
  return isToday ? 'Collected today' : `Collected ${istDateLabel(businessDate)}`;
}

export const MONEY_NO_CHAIN_TITLE = 'No first count yet';
export const MONEY_NO_CHAIN_BODY =
  "This branch's money day starts with one labeled first count. After it, every opening is carried - never typed.";
export const MONEY_NO_CHAIN_CAN_COUNT_LINE = 'You can record it now.';
export const MONEY_NO_CHAIN_ASK_LINE =
  'Ask your supervisor to record the first count.';
export const MONEY_TODO_EMPTY_TITLE = 'Nothing waiting.';

// ---- Close-sheet logic (M3; LES-015 conclusion headline) -------------------

export interface CloseConclusion {
  readonly kind: 'match' | 'difference' | 'incomplete';
  readonly headline: string;
  readonly differenceMinorUnits: number;
  readonly chipLabel: string | null;
}

/** The conclusion is STATED before the act (LES-015): match or a signed
 *  difference; never a question. countedRaw parses through the shared
 *  INR parser upstream -- this receives minor units or null. */
export function deriveCloseConclusion(
  derivedMinorUnits: number,
  countedMinorUnits: number | null,
): CloseConclusion {
  if (countedMinorUnits === null) {
    return {
      kind: 'incomplete',
      headline: 'Type the counted cash',
      differenceMinorUnits: 0,
      chipLabel: null,
    };
  }
  const difference = countedMinorUnits - derivedMinorUnits;
  if (difference === 0) {
    return {
      kind: 'match',
      headline: `Closes at ${formatRs(derivedMinorUnits)}. Tomorrow starts the same.`,
      differenceMinorUnits: 0,
      chipLabel: 'Matches',
    };
  }
  const magnitude = formatRs(Math.abs(difference)).replace(/^Rs\s/, 'Rs ');
  const shortOrOver = difference < 0 ? 'short' : 'over';
  return {
    kind: 'difference',
    headline: `Closes ${magnitude} ${shortOrOver} - stays on record with your reason.`,
    differenceMinorUnits: difference,
    chipLabel: `${magnitude} ${shortOrOver}`,
  };
}

// ---- Expense-sheet draft (LES-012: nothing typed is lost) ------------------

export interface MoneySheetDraft {
  readonly comment?: string;
  readonly kind: 'spent' | 'petty_cash';
  readonly category: string;
  readonly particulars: string;
  readonly paidTo: string;
  readonly amountRaw: string;
  readonly referenceNumber: string;
  readonly savedAtIso: string;
}

export function draftStorageKey(clinicId: number, businessDate: string, kind?: 'spent' | 'petty_cash'): string {
  return `money-desk-draft:${clinicId}:${businessDate}${kind ? ':'+kind : ''}`;
}

export function serializeDraft(draft: MoneySheetDraft): string {
  return JSON.stringify(draft);
}

export function parseDraft(raw: string | null): MoneySheetDraft | null {
  if (raw === null || raw === '') return null;
  try {
    const v = JSON.parse(raw) as Partial<MoneySheetDraft>;
    if (
      (v.kind !== 'spent' && v.kind !== 'petty_cash') ||
      typeof v.particulars !== 'string' ||
      typeof v.amountRaw !== 'string'
    ) {
      return null;
    }
    return {
      kind: v.kind,
      category: typeof v.category === 'string' ? v.category : '',
      particulars: v.particulars,
      ...(typeof v.comment === 'string' ? { comment: v.comment } : {}),
      paidTo: typeof v.paidTo === 'string' ? v.paidTo : '',
      amountRaw: v.amountRaw,
      referenceNumber:
        typeof v.referenceNumber === 'string' ? v.referenceNumber : '',
      savedAtIso: typeof v.savedAtIso === 'string' ? v.savedAtIso : '',
    };
  } catch {
    return null;
  }
}

/** A draft is worth keeping when the user typed anything at all. */
export function draftHasContent(
  draft: Pick<
    MoneySheetDraft,
    'category' | 'particulars' | 'comment' | 'paidTo' | 'amountRaw' | 'referenceNumber'
  >,
): boolean {
  return (
    (draft.comment?.trim() ?? '') !== '' ||
    draft.category.trim() !== '' ||
    draft.particulars.trim() !== '' ||
    draft.paidTo.trim() !== '' ||
    draft.amountRaw.trim() !== '' ||
    draft.referenceNumber.trim() !== ''
  );
}

// ---- Blocked-submit validation (M2: error beside the exact field) ---------

export interface ExpenseFieldErrors {
  readonly category?: string;
  readonly particulars?: string;
  readonly paidTo?: string;
  readonly amount?: string;
  readonly proof?: string;
}

export function validateExpenseSheet(input: {
  readonly kind: 'spent' | 'petty_cash';
  readonly category: string;
  readonly particulars: string;
  readonly paidTo: string;
  readonly amountMinorUnits: number | null;
  readonly hasPhoto: boolean;
  readonly referenceNumber: string;
  readonly approverPicked: boolean;
  /** FALSE when the org has NO configured C16 approval role (the
   *  fail-closed seed, PR #413 founder corrective): the OK path does not
   *  exist, so the message never offers it -- photo or voucher number
   *  are the only doors. */
  readonly approversAvailable: boolean;
}): ExpenseFieldErrors {
  const errors: Record<string, string> = {};
  if (input.kind === 'spent' && input.category.trim() === '') {
    errors.category = 'Pick a category';
  }
  if (input.particulars.trim() === '') {
    errors.particulars = 'Say what this was for';
  }
  if (input.kind === 'spent' && input.paidTo.trim() === '') {
    errors.paidTo = 'Who was paid?';
  }
  if (input.amountMinorUnits === null) {
    errors.amount = 'Enter the amount';
  }
  if (
    !input.hasPhoto &&
    input.referenceNumber.trim() === '' &&
    !(input.approversAvailable && input.approverPicked)
  ) {
    errors.proof = input.approversAvailable
      ? 'Take the photo, enter the voucher no., or pick the OK'
      : 'Take the photo or enter the voucher no.';
  }
  return errors;
}
